/**
 * Haptic Feedback Hook
 * Tactile feedback for mobile interactions
 */

import { useCallback, useRef } from 'react';

export type HapticPattern = 'light' | 'medium' | 'heavy' | 'success' | 'warning' | 'error';

const HAPTIC_PATTERNS: Record<HapticPattern, number[]> = {
  light: [10],
  medium: [25],
  heavy: [50],
  success: [10, 50, 10],
  warning: [30, 30, 30],
  error: [50, 100, 50],
};

interface HapticConfig {
  enabled: boolean;
  respectSystemSettings?: boolean;
}

export function useHapticFeedback(
  config: HapticConfig = { enabled: true, respectSystemSettings: true }
) {
  const lastTriggerRef = useRef<number>(0);
  const minInterval = 50; // Debounce rapid triggers

  const isSupported = useCallback(() => {
    return typeof navigator !== 'undefined' && 'vibrate' in navigator;
  }, []);

  const trigger = useCallback(
    (pattern: HapticPattern = 'medium') => {
      if (!config.enabled || !isSupported()) return false;

      const now = Date.now();
      if (now - lastTriggerRef.current < minInterval) return false;
      lastTriggerRef.current = now;

      try {
        const vibrationPattern = HAPTIC_PATTERNS[pattern];
        navigator.vibrate(vibrationPattern);
        return true;
      } catch {
        return false;
      }
    },
    [config.enabled, isSupported]
  );

  const triggerSwipeProgress = useCallback(
    (progress: number) => {
      // Subtle haptic at threshold crossings
      const thresholds = [0.25, 0.5, 0.75, 1.0];
      const crossedThreshold = thresholds.find(
        (t) => progress >= t && progress < t + 0.05
      );

      if (crossedThreshold) {
        const intensity = crossedThreshold === 1.0 ? 'heavy' : 'light';
        trigger(intensity);
      }
    },
    [trigger]
  );

  const triggerSwipeComplete = useCallback(
    (direction: 'left' | 'right') => {
      trigger(direction === 'right' ? 'success' : 'medium');
    },
    [trigger]
  );

  const triggerExpand = useCallback(() => {
    trigger('heavy');
  }, [trigger]);

  return {
    isSupported: isSupported(),
    trigger,
    triggerSwipeProgress,
    triggerSwipeComplete,
    triggerExpand,
  };
}

export default useHapticFeedback;
